import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { formatArticleDate, getPost } from '../blogPosts';

const Page = styled.main`
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--bg);
  color: var(--text);
`;

const Frame = styled(motion.div)`
  max-width: 46rem;
  width: 100%;
  margin: 0 auto;
  padding: 5rem 2rem 5.5rem;

  @media (max-width: 640px) {
    padding: 3rem 1.25rem 4rem;
  }
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 1.75rem;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.01em;
  color: var(--nav-muted);
  text-decoration: none;

  &:hover {
    color: var(--text);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 3px;
    border-radius: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Article = styled.article`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.3;
`;

const DateText = styled.time`
  display: block;
  margin: 0.4rem 0 0;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.01em;
  color: var(--text-muted);
`;

const Body = styled.p`
  margin: 1.35rem 0 0;
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 1.55;
  color: var(--text-muted);

  &:first-of-type {
    margin-top: 1.7rem;
  }
`;

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const paragraphs = [
    post.lead,
    ...post.paragraphs,
    ...post.sections.flatMap((section) => section.paragraphs),
  ].filter(Boolean);

  return (
    <Page>
      <Frame
        key={post.slug}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <Article>
          <Back to="/blog">
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M13 8H3M6.5 4.5 3 8l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            back
          </Back>
          <Title>{post.title}</Title>
          <DateText dateTime={post.date}>{formatArticleDate(post.date)}</DateText>
          {paragraphs.map((paragraph) => (
            <Body key={paragraph}>{paragraph}</Body>
          ))}
        </Article>
      </Frame>
    </Page>
  );
};

export default BlogPost;
